import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Modale, Riquadro, Spunta, Tabella } from '../../componenti/base.tsx'
import { GruppoScelta } from '../../componenti/persona-campi.tsx'
import * as posta from '../../servizi/posta.ts'
import { ConfermaApertura, TestoConCollegamenti, useAltezzaCornice, useCollegamentoToccato } from './CollegamentiMessaggio.tsx'
import { EtichettaProvenienza } from './EtichettaProvenienza.tsx'
import { Indirizzi } from './Indirizzi.tsx'
import { ComandiMessaggio } from './StampaMessaggio.tsx'
import { percorsoOriginaleArchiviato } from '../../servizi/posta-messaggio.ts'

/**
 * Archivio delle caselle (507): la linguetta per chi gestisce le utenze, la Direzione e la Referente
 * privacy.
 *
 * Le caselle archiviate con il conto alla rovescia del passaggio a Glacier, chi le ha archiviate e
 * quando; i comandi «Estendi», «Blocca il passaggio» e «Sblocca» finche' la casella e' nell'archivio
 * caldo, «Risveglia» quando e' in Glacier, «Riporta nell'archivio caldo» quando e' risvegliata. «Apri»
 * mostra l'indice (resta visibile anche in Glacier): si leggono i messaggi in sola lettura (ogni
 * apertura resta nel registro) e si scelgono quelli da estrarre. Ogni recupero da Glacier ha il suo
 * monitoraggio: chiesto il, stato, oggetti pronti, tempo trascorso e tempo stimato (dai tempi tipici di
 * Scaleway che l'API manda); la pagina si aggiorna da sola ogni minuto finche' c'e' un recupero aperto,
 * e il lavoro pianificato aggiorna lo stato a ogni giro (un quarto d'ora).
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const giorno = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
const giornoOra = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const data = (iso: string) => giorno.format(new Date(iso))
const quando = (iso: string) => giornoOra.format(new Date(iso))

/** «3 ore e 10 minuti», «2 giorni e 4 ore»: il tempo trascorso o che manca. */
export function durata(ms: number): string {
  const minuti = Math.max(0, Math.round(ms / 60_000))
  if (minuti < 60) return minuti === 1 ? '1 minuto' : `${minuti} minuti`
  const ore = Math.floor(minuti / 60)
  if (ore < 24) {
    const resto = minuti % 60
    return `${ore === 1 ? '1 ora' : `${ore} ore`}${resto ? ` e ${resto === 1 ? '1 minuto' : `${resto} minuti`}` : ''}`
  }
  const giorni = Math.floor(ore / 24)
  const resto = ore % 24
  return `${giorni === 1 ? '1 giorno' : `${giorni} giorni`}${resto ? ` e ${resto === 1 ? '1 ora' : `${resto} ore`}` : ''}`
}

const STATI_RECUPERO: Record<posta.StatoRecupero, { testo: string; tono: 'neutro' | 'accento' | 'positivo' | 'attenzione' | 'critico' }> = {
  in_coda: { testo: 'in coda', tono: 'neutro' },
  in_recupero: { testo: 'in recupero', tono: 'accento' },
  pronto: { testo: 'pronto', tono: 'positivo' },
  non_riuscito: { testo: 'non riuscito', tono: 'critico' },
  scaduto: { testo: 'consultazione finita', tono: 'neutro' },
  riportata: { testo: 'riportata nell’archivio caldo', tono: 'positivo' },
}

const aperto = (r: posta.RecuperoGlacier) => r.stato === 'in_coda' || r.stato === 'in_recupero'

export function ArchivioCaselle() {
  const [dati, setDati] = useState<posta.Archivio | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [aperta, setAperta] = useState<posta.CasellaArchiviata | null>(null)
  const [azione, setAzione] = useState<{ tipo: 'estendi' | 'blocca' | 'sblocca' | 'risveglia'; casella: posta.CasellaArchiviata } | null>(null)
  const [adesso, setAdesso] = useState(() => Date.now())

  const carica = useCallback(() => {
    posta.leggiArchivio().then((d) => { setDati(d); setErrore(null); setAdesso(Date.now()) })
      .catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(carica, [carica])

  // Finche' c'e' un recupero aperto la pagina si rilegge ogni minuto: lo stato lo aggiorna il lavoro.
  const inCorso = dati?.caselle.some((c) => c.recuperi.some(aperto)) ?? false
  useEffect(() => {
    if (!inCorso) return
    const t = window.setInterval(carica, 60_000)
    return () => window.clearInterval(t)
  }, [inCorso, carica])

  if (errore && !dati) return <Avviso tipo="errore">{errore}</Avviso>
  if (!dati) return <Attesa testo="Caricamento…" />
  const recuperi = dati.caselle.flatMap((c) => c.recuperi.map((r) => ({ casella: c, r })))
    .sort((a, b) => Number(aperto(b.r)) - Number(aperto(a.r)) || b.r.chiesto_il.localeCompare(a.r.chiesto_il))
  const tempi = dati.tempi_glacier

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}

      <Riquadro titolo="Archivio delle caselle" denso>
        <Avviso tipo="informazione">
          Una casella archiviata non riceve più posta (chi le scrive riceve un messaggio con il motivo) e non
          compare più nella Posta di chi la usava. Si consulta da qui in sola lettura: ogni messaggio aperto resta
          nel registro. Dopo il termine passa nell’archivio a lungo termine, che costa poco: l’elenco dei messaggi resta
          visibile, il contenuto si legge solo dopo un recupero, che richiede di solito
          {` ${tempi.tipicoOre}`} ore e al più {tempi.massimoOre}.
        </Avviso>
        <Tabella intestazioni={['Casella', 'Archiviata', 'Lungo termine', 'Messaggi',
                                <span className="solo-lettore" key="azioni">Azioni</span>]}
                 vuoto={<Vuoto testo="Nessuna casella archiviata." />}>
          {dati.caselle.map((c) => (
            <tr key={c.casella_id}>
              <td data-etichetta="Casella">
                <div className="voce-principale">{c.nome_visualizzato}</div>
                <div className="piccolo tenue">{c.indirizzo}</div>
                {c.dismessa_motivo && <div className="piccolo tenue">{posta.MOTIVI_DISMISSIONE[c.dismessa_motivo]}</div>}
              </td>
              <td data-etichetta="Archiviata" className="piccolo">
                {data(c.archiviata_il)}
                {c.archiviata_da && <div className="tenue">da {c.archiviata_da}</div>}
              </td>
              <td data-etichetta="Lungo termine" className="piccolo"><StatoGlacier c={c} /></td>
              <td data-etichetta="Messaggi" className="piccolo">
                {c.messaggi === 1 ? '1 messaggio' : `${c.messaggi} messaggi`}
                {c.estratti > 0 && <div className="tenue">{c.estratti} estratti</div>}
                {c.in_glacier > 0 && <div className="tenue">{c.in_glacier} nell’archivio a lungo termine</div>}
              </td>
              <td data-etichetta="Azioni">
                <div className="riga">
                  <Bottone dimensione="piccolo" onClick={() => { setMessaggio(null); setAperta(c) }}>Apri</Bottone>
                  {c.stato === 'caldo' && (
                    <>
                      <Bottone dimensione="piccolo" onClick={() => setAzione({ tipo: 'estendi', casella: c })}>Estendi</Bottone>
                      {c.bloccata
                        ? <Bottone dimensione="piccolo" onClick={() => setAzione({ tipo: 'sblocca', casella: c })}>Sblocca</Bottone>
                        : <Bottone dimensione="piccolo" onClick={() => setAzione({ tipo: 'blocca', casella: c })}>
                            Blocca il passaggio all’archivio a lungo termine
                          </Bottone>}
                    </>
                  )}
                  {c.stato !== 'caldo' && !c.risvegliata_fino_al && !c.recuperi.some((r) => r.tipo === 'casella' && aperto(r)) && (
                    <Bottone dimensione="piccolo" onClick={() => setAzione({ tipo: 'risveglia', casella: c })}>Risveglia</Bottone>
                  )}
                  {c.risvegliata_fino_al && !c.recuperi.some((r) => r.tipo === 'casella' && r.riporta_chiesta_il && r.stato === 'pronto') && (
                    <Bottone dimensione="piccolo" onClick={() => void riporta(c)}>Riporta nell’archivio caldo</Bottone>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>

      {recuperi.length > 0 && (
        <Riquadro titolo="Recuperi dall’archivio a lungo termine" denso>
          <p className="piccolo tenue">
            Lo stato si aggiorna da solo: il lavoro pianificato lo controlla ogni quarto d’ora, la pagina ogni minuto.
            Tempi del recupero: di solito {tempi.tipicoOre} ore, al più {tempi.massimoOre} dalla richiesta.
          </p>
          <Tabella intestazioni={['Casella', 'Richiesta', 'Stato', 'Tempo', 'Motivo']}>
            {recuperi.map(({ casella: c, r }) => (
              <tr key={r.id}>
                <td data-etichetta="Casella" className="piccolo">
                  <div className="voce-principale">{c.indirizzo}</div>
                  <div className="tenue">
                    {r.tipo === 'casella' ? `risveglio per ${r.giorni} giorni`
                      : r.messaggi === 1 ? 'estrazione di 1 messaggio' : `estrazione di ${r.messaggi} messaggi`}
                  </div>
                </td>
                <td data-etichetta="Richiesta" className="piccolo">
                  {quando(r.chiesto_il)}
                  {r.chiesto_da && <div className="tenue">da {r.chiesto_da}</div>}
                </td>
                <td data-etichetta="Stato" className="piccolo">
                  <Etichetta tono={STATI_RECUPERO[r.stato].tono}>{STATI_RECUPERO[r.stato].testo}</Etichetta>
                  <div className="tenue">{r.oggetti_pronti} di {r.oggetti} oggetti pronti</div>
                  {r.stato === 'pronto' && r.consultabile_fino_al && (
                    <div className="tenue">consultabile fino al {quando(r.consultabile_fino_al)}</div>
                  )}
                  {r.riporta_chiesta_il && r.stato === 'pronto' && <div className="tenue">ritorno nell’archivio caldo in corso</div>}
                  {r.errore && <div className="testo-critico">{r.errore}</div>}
                </td>
                <td data-etichetta="Tempo" className="piccolo"><TempoRecupero r={r} adesso={adesso} /></td>
                <td data-etichetta="Motivo" className="piccolo">{r.motivo}</td>
              </tr>
            ))}
          </Tabella>
        </Riquadro>
      )}

      {aperta && (
        <IndiceCasella casella={dati.caselle.find((c) => c.casella_id === aperta.casella_id) ?? aperta}
          chiudi={() => setAperta(null)}
          estratti={(testo) => { setMessaggio(testo); carica() }} />
      )}
      {azione && (
        <AzioneConMotivo tipo={azione.tipo} casella={azione.casella} giorniRisveglio={dati.giorni_risveglio}
          chiudi={() => setAzione(null)}
          fatto={(testo) => { setAzione(null); setMessaggio(testo); carica() }} />
      )}
    </>
  )

  async function riporta(c: posta.CasellaArchiviata) {
    setMessaggio(null)
    try {
      await posta.riportaInArchivioCaldo(c.casella_id)
      setMessaggio(`${c.indirizzo} torna nell’archivio caldo: il lavoro pianificato porta fuori dall’archivio a lungo termine testi e allegati.`)
      carica()
    } catch (e) { setErrore(testoErrore(e)) }
  }
}

function StatoGlacier({ c }: { c: posta.CasellaArchiviata }) {
  if (c.stato === 'caldo') {
    if (c.bloccata) {
      return (
        <>
          <Etichetta tono="attenzione">passaggio bloccato</Etichetta>
          <div className="tenue">da {c.bloccata.da ?? '—'} il {data(c.bloccata.il)}: {c.bloccata.motivo}</div>
        </>
      )
    }
    const n = c.giorni_a_glacier ?? 0
    return (
      <>
        <Etichetta tono="accento">{n === 0 ? 'passa nell’archivio a lungo termine stanotte' : `passa nell’archivio a lungo termine fra ${n === 1 ? '1 giorno' : `${n} giorni`}`}</Etichetta>
        <div className="tenue">il {data(c.glacier_dal)}</div>
      </>
    )
  }
  if (c.stato === 'in_glacier') return <Etichetta tono="accento">passaggio nell’archivio a lungo termine in corso</Etichetta>
  return (
    <>
      <Etichetta>a lungo termine</Etichetta>
      {c.in_glacier_il && <div className="tenue">dal {data(c.in_glacier_il)}</div>}
      {c.risvegliata_fino_al && <div className="tenue">risvegliata: consultabile fino al {quando(c.risvegliata_fino_al)}</div>}
    </>
  )
}

function TempoRecupero({ r, adesso }: { r: posta.RecuperoGlacier; adesso: number }) {
  const chiesto = new Date(r.chiesto_il).getTime()
  if (!aperto(r)) {
    return r.pronto_il ? <>pronto dopo {durata(new Date(r.pronto_il).getTime() - chiesto)}</> : <>—</>
  }
  const stimato = new Date(r.stimato_il).getTime()
  const massimo = new Date(r.massimo_il).getTime()
  return (
    <>
      trascorse {durata(adesso - chiesto)}
      <div className="tenue">
        {adesso < stimato ? `stima: ancora circa ${durata(stimato - adesso)}`
          : adesso < massimo ? `oltre il tempo tipico; al più entro ${quando(r.massimo_il)}`
            : 'oltre il tempo massimo previsto: avvisa chi gestisce il sistema'}
      </div>
    </>
  )
}

/** Estendi, blocca, sblocca, risveglia: un motivo, e i giorni quando servono. */
function AzioneConMotivo({ tipo, casella, giorniRisveglio, chiudi, fatto }: {
  tipo: 'estendi' | 'blocca' | 'sblocca' | 'risveglia'; casella: posta.CasellaArchiviata; giorniRisveglio: number
  chiudi: () => void; fatto: (testo: string) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [giorni, setGiorni] = useState(String(tipo === 'risveglia' ? giorniRisveglio : 10))
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const testi = {
    estendi: { titolo: `Estendere il termine di ${casella.indirizzo}`, comando: 'Estendi',
      spiega: 'Il passaggio all’archivio a lungo termine si sposta in avanti dei giorni indicati.' },
    blocca: { titolo: `Bloccare il passaggio all’archivio a lungo termine di ${casella.indirizzo}`, comando: 'Blocca',
      spiega: 'La casella resta nell’archivio caldo finché qualcuno non sblocca il passaggio.' },
    sblocca: { titolo: `Sbloccare il passaggio all’archivio a lungo termine di ${casella.indirizzo}`, comando: 'Sblocca',
      spiega: 'Se il termine è già passato, la casella va nell’archivio a lungo termine la notte prossima.' },
    risveglia: { titolo: `Risvegliare ${casella.indirizzo}`, comando: 'Risveglia',
      spiega: 'Testi e allegati tornano consultabili per i giorni indicati, di solito entro 12 ore e al più entro 24 '
        + '(il recupero ha un costo per gigabyte). Poi si possono estrarre messaggi o riportare la casella nell’archivio caldo.' },
  }[tipo]
  const conGiorni = tipo === 'estendi' || tipo === 'risveglia'

  async function conferma() {
    setInCorso(true); setErrore(null)
    try {
      const n = Number(giorni)
      if (tipo === 'estendi') {
        const r = await posta.estendiArchivio(casella.casella_id, n, motivo)
        fatto(`${casella.indirizzo}: passa nell’archivio a lungo termine il ${data(r.glacier_dal)}.`)
      } else if (tipo === 'blocca') {
        await posta.bloccaArchivio(casella.casella_id, motivo)
        fatto(`${casella.indirizzo}: passaggio all’archivio a lungo termine bloccato.`)
      } else if (tipo === 'sblocca') {
        await posta.sbloccaArchivio(casella.casella_id, motivo)
        fatto(`${casella.indirizzo}: passaggio all’archivio a lungo termine sbloccato.`)
      } else {
        await posta.risvegliaCasella(casella.casella_id, n, motivo)
        fatto(`${casella.indirizzo}: risveglio chiesto. Lo stato si segue in «Recuperi dall’archivio a lungo termine».`)
      }
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo={testi.titolo} chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} disabled={motivo.trim().length < 5}
                   onClick={() => void conferma()}>{testi.comando}</Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p>{testi.spiega}</p>
      {conGiorni && (
        <Campo etichetta="Giorni" tipo="number" min={1} max={tipo === 'risveglia' ? 30 : 365}
               valore={giorni} cambia={setGiorni} obbligatorio />
      )}
      <AreaTesto etichetta="Motivo" valore={motivo} cambia={setMotivo} obbligatorio maxLength={1000}
                 aiuto="Resta nel registro con il tuo nome." />
    </Modale>
  )
}

/** L'indice della casella: lettura in sola lettura, scelta dei messaggi da estrarre. */
function IndiceCasella({ casella, chiudi, estratti }: {
  casella: posta.CasellaArchiviata; chiudi: () => void; estratti: (testo: string) => void
}) {
  const [pagine, setPagine] = useState<posta.PaginaIndice[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  const [letto, setLetto] = useState<string | null>(null)
  const [estrai, setEstrai] = useState(false)

  const carica = useCallback((primaDi: { data: string; id: string } | null) => {
    posta.indiceArchivio(casella.casella_id, primaDi)
      .then((p) => setPagine((prima) => (primaDi ? [...prima, p] : [p])))
      .catch((e) => setErrore(testoErrore(e)))
  }, [casella.casella_id])
  useEffect(() => carica(null), [carica])

  const messaggi = useMemo(() => pagine.flatMap((p) => p.messaggi), [pagine])
  const prossimo = pagine.at(-1)?.prossimo ?? null
  const alterna = (id: string, v: boolean) => setScelti((s) => {
    const n = new Set(s)
    if (v) n.add(id); else n.delete(id)
    return n
  })

  return (
    <Modale titolo={`Archivio: ${casella.indirizzo}`} chiudi={chiudi} larga
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Chiudi</Bottone>
          <Bottone variante="primario" disabled={!scelti.size} onClick={() => setEstrai(true)}>
            Estrai {scelti.size ? `(${scelti.size})` : ''}
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p className="piccolo">
        Sola lettura: ogni messaggio aperto resta nel registro. I messaggi estratti restano consultabili e non vanno
        nell’archivio a lungo termine; quelli che ci sono già si estraggono con un recupero, che si segue in
        «Recuperi dall’archivio a lungo termine».
      </p>
      {!pagine.length ? <Attesa testo="Caricamento…" /> : (
        <Tabella intestazioni={[<span className="solo-lettore" key="scelta">Scelta</span>, 'Data', 'Da', 'Oggetto', 'Stato']}
                 vuoto={<Vuoto testo="La casella non ha messaggi." />}>
          {messaggi.map((m) => (
            <tr key={m.id}>
              <td data-etichetta="Scelta">
                <Spunta etichetta={<span className="solo-lettore">Scegli {m.oggetto}</span>}
                        valore={scelti.has(m.id)} cambia={(v) => alterna(m.id, v)}
                        disabilitato={m.estratto || m.in_recupero} />
              </td>
              <td data-etichetta="Data" className="piccolo">{quando(m.data)}</td>
              <td data-etichetta="Da" className="piccolo" title={m.da}>
                {posta.nomeBreve(m.da)} <EtichettaProvenienza messaggio={m} />
              </td>
              <td data-etichetta="Oggetto">
                <Bottone variante="discreto" dimensione="piccolo" onClick={() => setLetto(m.id)}>
                  {m.oggetto || '(senza oggetto)'}
                </Bottone>
                {m.cartella && <div className="piccolo tenue">{m.cartella}</div>}
              </td>
              <td data-etichetta="Stato" className="piccolo">
                {m.estratto && <Etichetta tono="positivo">estratto</Etichetta>}
                {m.in_recupero && <Etichetta tono="accento">in recupero</Etichetta>}
                {!m.estratto && m.in_glacier && <Etichetta>a lungo termine</Etichetta>}
                {m.ha_allegati && <div className="tenue">con allegati</div>}
              </td>
            </tr>
          ))}
        </Tabella>
      )}
      {prossimo && (
        <div className="riga"><Bottone dimensione="piccolo" onClick={() => carica(prossimo)}>Altri messaggi</Bottone></div>
      )}
      {letto && <MessaggioInArchivio casellaId={casella.casella_id} postaId={letto} chiudi={() => setLetto(null)} />}
      {estrai && (
        <ConfermaEstrazione casella={casella} messaggi={[...scelti]} chiudi={() => setEstrai(false)}
          fatto={(testo) => { setEstrai(false); setScelti(new Set()); carica(null); estratti(testo) }} />
      )}
    </Modale>
  )
}

function ConfermaEstrazione({ casella, messaggi, chiudi, fatto }: {
  casella: posta.CasellaArchiviata; messaggi: string[]; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  async function conferma() {
    setInCorso(true); setErrore(null)
    try {
      const r = await posta.estraiDallArchivio(casella.casella_id, messaggi, motivo)
      const parti = []
      if (r.estratti) parti.push(r.estratti === 1 ? '1 messaggio estratto' : `${r.estratti} messaggi estratti`)
      if (r.in_recupero) {
        parti.push(`${r.in_recupero === 1 ? '1 messaggio è' : `${r.in_recupero} messaggi sono`} nell’archivio a lungo termine: recupero chiesto, `
          + 'lo stato si segue in «Recuperi dall’archivio a lungo termine»')
      }
      fatto(`${casella.indirizzo}: ${parti.join('; ') || 'niente da estrarre'}.`)
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo={`Estrarre ${messaggi.length === 1 ? '1 messaggio' : `${messaggi.length} messaggi`}`} chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} disabled={motivo.trim().length < 5}
                   onClick={() => void conferma()}>Estrai</Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p>
        I messaggi estratti restano consultabili da qui e non vanno nell’archivio a lungo termine. Quelli già nell’archivio a lungo termine tornano dopo un
        recupero, di solito entro 12 ore; chi lo chiede riceve un avviso quando sono pronti.
      </p>
      <AreaTesto etichetta="Motivo" valore={motivo} cambia={setMotivo} obbligatorio maxLength={1000}
                 aiuto="Resta nel registro con il tuo nome." />
    </Modale>
  )
}

function MessaggioInArchivio({ casellaId, postaId, chiudi }: { casellaId: string; postaId: string; chiudi: () => void }) {
  const [m, setM] = useState<posta.MessaggioArchiviato | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    posta.messaggioArchiviato(casellaId, postaId).then(setM).catch((e) => setErrore(testoErrore(e)))
  }, [casellaId, postaId])
  const corpo = useMemo(() => (m?.html ? posta.ripulisciHtml(m.html, false) : null), [m?.html])
  const pagina = useMemo(() => (corpo ? posta.paginaMessaggio(corpo.html, false) : ''), [corpo])
  const cornice = useRef<HTMLIFrameElement>(null)
  const collegamento = useCollegamentoToccato(cornice)
  useAltezzaCornice(cornice)

  async function scarica(allegatoId: string) {
    try {
      const { url } = await posta.allegatoArchiviato(casellaId, allegatoId)
      window.location.assign(url)
    } catch (e) { setErrore(testoErrore(e)) }
  }

  return (
    <Modale titolo={m ? m.oggetto || '(senza oggetto)' : 'Messaggio'} chiudi={chiudi} larga
      azioni={<Bottone variante="discreto" onClick={chiudi}>Chiudi</Bottone>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!m && !errore && <Attesa testo="Caricamento…" />}
      {m && (
        <>
          {/* Gli indirizzi con «Copia», come nella webmail (2/11/2026). */}
          <Indirizzi da={m.da} a={m.a} cc={m.cc} coda={<EtichettaProvenienza messaggio={m} />} />
          {m.allegati.length > 0 && (
            // Subito sotto Da, A e Cc, prima dei comandi, come nella webmail (4/11/2026).
            <ul className="archivio-allegati">
              {m.allegati.map((a) => (
                <li key={a.id}>
                  {a.scaricabile
                    ? <Bottone variante="discreto" dimensione="piccolo" onClick={() => void scarica(a.id)}>{a.nome}</Bottone>
                    : <span>{a.nome}</span>}
                  <span className="piccolo tenue"> · {Math.max(1, Math.round(a.dimensione / 1024))} KB
                    {a.in_glacier && !a.scaricabile && a.antivirus !== 'infetto' ? ' · nell’archivio a lungo termine' : ''}</span>
                  {/* Infetto (546): in quarantena o bloccato, senza comando, come nella webmail. */}
                  {a.quarantena
                    ? <span className="piccolo testo-critico"> · {posta.scrittaQuarantena(a.quarantena)}</span>
                    : a.antivirus === 'infetto' && <span className="piccolo testo-critico"> · bloccato dall’antivirus</span>}
                </li>
              ))}
            </ul>
          )}
          {/* Stampa, PDF e .eml come nella webmail (2/11/2026); con il testo in Glacier il .eml lo rifiuta l'API. */}
          <ComandiMessaggio percorsoEml={posta.percorsoEmlArchiviato(casellaId, m.id)}
            percorsoOriginale={percorsoOriginaleArchiviato(casellaId, m.id)} messaggio={{
            oggetto: m.oggetto, da: m.da, a: m.a, cc: m.cc, data: m.data, allegati: m.allegati, html: m.html,
            testo: m.testo_in_glacier ? '(Il testo è nell’archivio a lungo termine: si legge dopo un risveglio della casella o '
              + 'l’estrazione del messaggio.)' : m.testo,
          }} />
          <p className="piccolo">
            {quando(m.data)}
            {m.estratto && <> · <Etichetta tono="positivo">estratto</Etichetta></>}
          </p>
          {m.testo_in_glacier ? (
            <Avviso tipo="informazione">
              Il testo è nell’archivio a lungo termine: si legge dopo un risveglio della casella o l’estrazione del messaggio.
            </Avviso>
          ) : corpo ? (
            // Come nella webmail: HTML ripulito, documento isolato con il solo script dei collegamenti,
            // niente immagini remote; il collegamento toccato apre la finestra di conferma.
            <>
              <iframe ref={cornice} className="posta-html" sandbox="allow-scripts" title="Corpo del messaggio"
                      srcDoc={pagina} />
              {collegamento.scelto && <ConfermaApertura verifica={collegamento.scelto} chiudi={collegamento.chiudi} />}
            </>
          ) : (
            <TestoConCollegamenti testo={m.testo || '(messaggio senza testo)'} />
          )}
        </>
      )}
    </Modale>
  )
}

/** «Archivia» dalla linguetta «Caselle e ricezione»: il motivo, poi la conferma. */
export function ConfermaArchiviazione({ casella, chiudi, archiviata, motivoIniziale, azione = 'Archivia' }: {
  casella: { id: string; indirizzo: string }; chiudi: () => void; archiviata: (testo: string) => void
  /** Motivo gia' scelto, che si puo' cambiare: per le caselle di gruppo l'indirizzo non piu' in uso. */
  motivoIniziale?: posta.MotivoDismissione
  /** Testo del comando: «Disattiva e archivia» nelle caselle di gruppo (richiesta di Carmem, 1/11/2026). */
  azione?: string
}) {
  const [motivo, setMotivo] = useState<posta.MotivoDismissione | ''>(motivoIniziale ?? '')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  async function conferma() {
    if (!motivo) return
    setInCorso(true); setErrore(null)
    try {
      await posta.archiviaCasella(casella.id, motivo)
      archiviata(`${casella.indirizzo} è nell’archivio delle caselle: non riceve più posta e passa nell’archivio a lungo termine fra 10 giorni.`)
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo={`Archiviare ${casella.indirizzo}`} chiudi={chiudi} fuocoIniziale="corpo"
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} disabled={!motivo} onClick={() => void conferma()}>{azione}</Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p>
        La casella si disattiva: chi le scrive riceve un rifiuto con il motivo scelto, in italiano e in inglese, e
        l’indirizzo di contatto generale di CIDIS. Esce dalla Posta di chi la usava e passa nell’archivio delle caselle,
        dove la Direzione, la Referente privacy e chi gestisce le utenze la consultano in sola lettura. Dopo 10 giorni
        passa nell’archivio a lungo termine, salvo estensione o blocco.
      </p>
      <GruppoScelta legenda="Motivo" obbligatorio valore={motivo}
        cambia={(v) => setMotivo(v as posta.MotivoDismissione)}
        opzioni={(Object.keys(posta.MOTIVI_DISMISSIONE) as posta.MotivoDismissione[])
          .map((m) => ({ valore: m, etichetta: posta.MOTIVI_DISMISSIONE[m] }))} />
    </Modale>
  )
}
